import React, { useEffect, useRef, useState } from "react";
import { useUser } from "../../context/UserContext";
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from "../UI/Button";

const FaceVerificationPage = ({ type = "entry", location = "main", onVerificationSuccess, checkpoint }) => {
    const videoRef = useRef(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cameraOn, setCameraOn] = useState(false);
    const [photos, setPhotos] = useState([]);

    const { user, logout } = useUser();

    useEffect(() => {
        if (result?.match && result.user) {
            if (onVerificationSuccess) {
                onVerificationSuccess(result.user);
            }
        }
    }, [result]);

    useEffect(() => {
        return () => stopCamera();
    }, []);
    
    const MAX_PHOTOS = 5;

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            setCameraOn(true);
            setResult(null);
            setPhotos([]);
        } catch (err) {
            console.error("Ошибка запуска камеры:", err);
        }
    };

    const stopCamera = () => {
        const stream = videoRef.current?.srcObject;
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
        if (videoRef.current) videoRef.current.srcObject = null;
        setCameraOn(false);
    };

    const capturePhoto = () =>
        new Promise((resolve) => {
            const canvas = document.createElement("canvas");
            const video = videoRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext("2d").drawImage(video, 0, 0);
            canvas.toBlob((blob) => {
                const file = new File([blob], `photo.jpg`, {
                    type: "image/jpeg",
                });
                resolve(file);
            }, "image/jpeg");
        });

    const verifyFace = async () => {
        setLoading(true);
        setResult(null);
        setPhotos([]);

        const tempPhotos = [];
        for (let i = 0; i < MAX_PHOTOS; i++) {
            const photo = await capturePhoto();
            tempPhotos.push(photo);
            await new Promise((r) => setTimeout(r, 300));
        }

        const formData = new FormData();
        tempPhotos.forEach((photo) => formData.append("images", photo));
        formData.append("type", type);
        formData.append("location", location);
        formData.append("checkpointId", checkpoint._id);
        formData.append("checkpointName", checkpoint.name);

        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/verify`, 
                {
                    method: "POST",
                    body: formData,
                },
                logout
            );

            const data = await res.json();
            setResult(data);
        } catch (err) {
            setResult({ error: "Ошибка при верификации" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 text-white">
            <video
                ref={videoRef}
                autoPlay
                className="w-full max-w-md rounded border"
            />

            <div className="flex justify-between max-w-md">
                {!cameraOn ? (
                    <Button
                        onClick={startCamera}
                        variant="primary"
                    >
                        Запустить камеру
                    </Button>
                ) : (
                    <>
                        <Button
                            onClick={stopCamera}
                            variant="danger"
                        >
                            Остановить
                        </Button>
                        {!loading ? (
                            <Button
                                onClick={verifyFace}
                                variant="primary"
                            >
                                Проверить лицо
                            </Button>
                        ) : (
                            <div className="inline-block align-middle mr-10">
                                <svg
                                    className="animate-spin h-6 w-6 text-green-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                    />
                                </svg>
                            </div>
                        )}
                    </>
                )}
            </div>

            {result?.match && result.user && (
                <div className="mt-4 p-4 border rounded bg-[#1E1E1E] flex items-center space-x-4">
                    <img
                        src={`${process.env.REACT_APP_API_URL}${result.user.photoPath}`}
                        alt="Фото"
                        className="w-16 h-16 rounded object-cover border"
                    />
                    <div>
                        <p>
                            Пользователь: {result.user.username}{" "}
                            {result.user.firstName} {result.user.lastName}
                        </p>
                        <p>Точность: {result.distance.toFixed(4)}</p>
                    </div>
                </div>
            )}

            {result && !result.match && (               
                <div className="text-red-500 mt-4 flex justify-between items-center">
                    <p>❌ Совпадение не найдено</p>
                    <p>Откройте приложение и отсканируйте NFC метку.</p>
                </div>               
            )}

            {result?.error && (
                <p className="text-red-500 mt-4">❌ {result.error}</p>
            )}
        </div>
    );
};

export default FaceVerificationPage;
